import {
    ChangeDetectorRef,
    Directive,
    ElementRef,
    forwardRef,
    HostBinding,
    HostListener,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    Renderer2,
    Type
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {ConnectableObservable, merge, MonoTypeOperatorFunction, Observable, of, Subject} from 'rxjs';
import {debounceTime, filter, map, publish, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import {destroy$} from '../destroyable';
import {ModalService} from '../modal';
import {AutocompleteConfig, getAutocompleteDisplayValue, getAutocompleteValue} from './autocomplete-config';
import {AutocompleteListComponent, DisplayVariant} from './autocomplete-list.component';
import {AutocompleteService} from './autocomplete.service';

const DEFAULT_DEBOUNCE_MS = 250;

@Directive({
    selector: 'input[autocompleteSearch]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AutocompleteDirective),
            multi: true
        }
    ],
    standalone: false
})
export class AutocompleteDirective<VariantType = any, DisplayVariantType extends DisplayVariant = any>
    implements ControlValueAccessor, OnInit, OnDestroy {

    @Input('autocompleteSearch')
    public config: AutocompleteConfig;

    @Input()
    public autocompleteReferenceElement: HTMLElement;

    @HostBinding('value') // Is primary value binding
    public displayTerm: string = '';

    public propagateChange: (value: VariantType) => void;

    public propagateTouch: () => void;

    public readonly destroy$ = destroy$();

    public listComponent: AutocompleteListComponent<VariantType, DisplayVariantType>;

    protected variants: VariantType[];

    protected readonly listComponentClass: Type<AutocompleteListComponent<VariantType, DisplayVariantType>> =
                           AutocompleteListComponent;

    /**
     * Stream of terms.
     * New term is emitting for each term modification.
     * @type {Subject<string>}
     */
    protected readonly term$ = new Subject<string>();

    private term: string = '';

    /**
     * Stream of "Ctrl+Space pressed" events with the current term as payload.
     * @type {Subject<string>}
     */
    private readonly forcedTerm$ = new Subject<string>();

    /**
     * Stream of the "Enter key pressed" events.
     * @type {Subject<KeyboardEvent>}
     */
    private readonly enterKey$ = new Subject<KeyboardEvent>();

    private value: VariantType;

    /**
     * Indicates what term was modified since the last variants was loaded.
     * @type {boolean}
     */
    private isTermDraft: boolean = false;

    /**
     * Stream of variants for the term.
     * New variants are emitting for each term modification.
     * @type {ConnectableObservable<*[]>}
     */
    private variants$: ConnectableObservable<VariantType[]>;

    private isInFocus: boolean = false;

    /**
     * Storing previous term allows to reset it when list closed.
     */
    private previousTerm: string;

    private isInitialized: boolean = false;

    constructor(
        protected inputElement: ElementRef,
        protected modalService: ModalService,
        protected autocompleteService: AutocompleteService,
        protected changeDetector: ChangeDetectorRef,
        protected renderer: Renderer2,
        protected injector: Injector
    ) {
        this.listenToTerms();
        this.listenToEnterKey();
    }

    @HostListener('input', ['$event.target'])
    public onInput(target: EventTarget | null): void {
        const value = (target as HTMLInputElement).value;

        this.setTerm(value, false);
        this.term$.next(value);

        if (this.config.freeTyping) {
            this.value = value as any;
            this.propagateChange(this.value);
        }
    }

    @HostListener('keydown', ['$event'])
    public onKeyDown(event: KeyboardEvent): void {
        const KEY_UP     = 38;
        const KEY_DOWN   = 40;
        const KEY_ENTER  = 13;
        const KEY_SPACE  = 32;
        const KEY_ESCAPE = 27;

        /* tslint:disable:cyclomatic-complexity */
        switch (event.keyCode) {
            case KEY_UP:
                if (this.listComponent) {
                    event.preventDefault();
                    this.listComponent.prevVariant();
                }

                break;
            case KEY_DOWN:
                if (this.listComponent) {
                    event.preventDefault();
                    this.listComponent.nextVariant();
                }

                break;
            case KEY_ENTER:
                this.enterKey$.next(event);

                break;
            case KEY_SPACE:
                if (event.ctrlKey && !this.listComponent) {
                    event.preventDefault();
                    this.forcedTerm$.next(this.term);
                }

                break;
            case KEY_ESCAPE:
                if (this.listComponent) {
                    event.preventDefault();
                    this.close();
                }
                this.applyTerm();

                break;
        }
        /* tslint:enable:cyclomatic-complexity */
    }

    @HostListener('focus')
    public onFocus(): void {
        this.isInFocus = true;

        // check if the focus happened not due to clicking on item of the list
        if (!this.listComponent) {
            if (this.config.selectAllOnFocus) {
                this.selectAllText();
            }
        }
    }

    @HostListener('blur')
    public onBlur(): void {
        this.isInFocus = false;

        // check if the blur happened not due to list clicking (also as a side effect skips tab press when list is open)
        if (!this.listComponent) {
            this.applyTerm();
        }
    }

    public ngOnInit(): void {
        this.isInitialized = true;
        this.updateTermByValue();
    }

    public ngOnDestroy(): void {
        this.close();
    }

    public writeValue(value: VariantType): void {
        this.value = value;

        if (this.isInitialized) {
            this.updateTermByValue();
        }
    }

    public registerOnChange(fn: (value: VariantType) => void): void {
        this.propagateChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.propagateTouch = fn;
    }

    public selectVariantForCurrentTerm(): void {
        if (this.term) {
            this.isTermDraft = true;
            this.enterKey$.next(undefined);
            this.forcedTerm$.next(this.term);
        }
    }

    /**
     * Opens the list without filtering by the current term.
     */
    public openSelectDropdown(): void {
        (this.inputElement.nativeElement as HTMLInputElement).focus();

        // Select all text. If selectAllOnFocus is true, then text is selected by focus listener.
        if (!this.config.selectAllOnFocus) {
            this.selectAllText();
        }

        // Force dropdown open, but without filtering by the current term.
        this.forcedTerm$.next('');
    }

    protected setVariant(variant: VariantType): void {
        if (variant != null || !this.config.disallowEmptySelect) {
            this.writeValue(variant);
            this.propagateChange(variant);
        }
    }

    protected applyTerm(): void {
        if (this.config.disallowEmptySelect) {
            this.updateTermByValue();
        } else if (this.config.allowVariantOrNull) {
            if (this.term) {
                this.updateTermByValue();
            } else {
                this.setVariant(null);
            }
        }
    }

    protected listDestroyed(): void {
        this.listComponent = null;
        this.previousTerm = null; // No matter that was the previous term, we should be able to open list again.
    }

    protected transformRawVariants(rawVariants: any[]): VariantType[] {
        return rawVariants && rawVariants.map(rawVariant => getAutocompleteValue(rawVariant, this.config));
    }

    protected open(): void {
        let listComponent = this.listComponent;

        if (!listComponent) {
            const modalRef = this.modalService.open(this.listComponentClass, {
                injector:    this.injector,
                destroyWhen: this.destroy$,
                onDestroy:   () => this.listDestroyed(),
                configure:   {
                    config:           this.config,
                    inputElement:     this.inputElement,
                    referenceElement: this.autocompleteReferenceElement || this.inputElement.nativeElement
                }
            });

            listComponent = this.listComponent = modalRef.instance;

            merge(
                listComponent.onSelect.pipe(
                    tap(this.setVariant.bind(this))
                ),
                listComponent.onClose.pipe(
                    tap(() => this.applyTerm())
                )
            ).pipe(
                takeUntil(modalRef.destroy$)
            ).subscribe();
        }

        this.setListComponentVariants();
        this.changeDetector.markForCheck();
    }

    protected setListComponentVariants(): void {
        this.listComponent.setVariants(this.variants);
    }

    protected getRawVariantsByTerm(term: string): Observable<any[]> {
        return this.autocompleteService.simpleSearch(term, this.config.type);
    }

    protected updateTermByValue(): void {
        this.setTerm(getAutocompleteDisplayValue(this.value, this.config), true);
    }

    private listenToTerms(): void {
        let lastRequestedTerm: string;
        let variantsTerm: string;

        const term$: Observable<string> = merge(
            this.term$.pipe(
                this.distinctUntilTermChanged(),
                tap(() => this.isTermDraft = true),
                tap(term => lastRequestedTerm = term),
                debounceTime(DEFAULT_DEBOUNCE_MS)
            ),
            this.forcedTerm$.pipe(
                tap(() => this.isTermDraft = true),
                tap(term => lastRequestedTerm = term)
            )
        ).pipe(
            tap(term => variantsTerm = term)
        );

        this.variants$ = term$.pipe(
            switchMap(term => term || this.config.allowVariantsForEmptyTerm
                ? this.getRawVariantsByTerm(term)
                : of(null)
            ),
            map(rawVariant => this.transformRawVariants(rawVariant)),
            tap(() => this.isTermDraft = (lastRequestedTerm !== variantsTerm)),
            tap(innerVariants => this.openWithVariants(innerVariants)),
            takeUntil(this.destroy$),
            publish()
        ) as ConnectableObservable<VariantType[]>;

        this.variants$.connect();
    }

    private listenToEnterKey(): void {
        this.enterKey$.pipe(
            filter(() => !!(this.isTermDraft || this.listComponent)),
            tap((event: KeyboardEvent) => event && event.preventDefault()),
            switchMap(() => this.isTermDraft ? this.variants$.pipe(take(1)) : of(this.variants))
        ).subscribe(() => this.listComponent && this.listComponent.selectVariant());
    }

    private setTerm(value: string, updateDisplay: boolean = true): void {
        this.term = value;

        if (updateDisplay) {
            const oldDisplayTerm: string = this.displayTerm;

            this.displayTerm = value;
            this.changeDetector.markForCheck(); // force DOM updating

            if (oldDisplayTerm === value) { // if there is no binding changes then update the input's value manually
                this.renderer.setProperty(this.inputElement.nativeElement, 'value', this.displayTerm);
            }
        }
    }

    private openWithVariants(variants: VariantType[]): void {
        this.variants = variants;

        if (this.isInFocus) {
            variants
                ? this.open()
                : this.close();
        }
    }

    private close(): void {
        const listComponent = this.listComponent;

        if (listComponent) {
            listComponent.close();
        }
    }

    private selectAllText(): void {
        const textLength: number = this.inputElement.nativeElement.value.length;

        this.inputElement.nativeElement
            .setSelectionRange(0, textLength); // "select all"
    }

    private distinctUntilTermChanged(): MonoTypeOperatorFunction<string> {
        return term$ => term$.pipe(
            filter(term => term !== this.previousTerm),
            tap(term => this.previousTerm = term)
        );
    }

}
